import React, { useState } from "react";
import { Button, Text } from "@showitinc/showit-wc";
import _ from "lodash";
import UIActions from "../actions/UIActions";
import UserActions from "../actions/UserActions";
import AppConstants from "../constants/AppConstants";
import LoadingSpinner from "./LoadingSpinner";
import LibrarySiteChooserThumbnail from "./LibrarySiteChooserThumbnail";
import LibrarySitePreview from "./LibrarySitePreview";
import ShowitNameLogo from "./ShowitNameLogo";
import SubviewError from "./SubviewError";
import SubviewProcessing from "./SubviewProcessing";
import DesignUtils from "../utils/DesignUtils";
import DesignManager from "../managers/DesignManager";
import useShowitLibraryDesigns from "../hooks/query/useShowitLibraryDesigns";
import useUserLibraryDesigns from "../hooks/query/useUserLibraryDesigns";
import useSites from "../hooks/query/useSites";
import SiteActions from "../actions/SiteActions";
import UserService from "../service/UserService";
import { queryClient } from "../utils/QueryUtils";
import { USER_SITES } from "../constants/QueryKeys";
import InfoMessageModal from "./InfoMessageModal";
import useOpenAccount, { PATH_SUBSCRIBE } from "../hooks/useOpenAccount";
import { isCorrectVersion, migrateDesign } from "../utils/DesignVersionUtils";
import config from "../config/app";
import API from "../service/API";
import { MIGRATION_TYPE_SHARED } from "../constants/MigrationConstants";
import uiPrefix from "../utils/UiPrefixUtils";
import styles from "./LibrarySiteChooserView.module.css";
import { prepareCreationData } from "@/utils/LibraryUtils";

const {
  LIBRARY_VIEW_INITIAL,
  LIBRARY_VIEW_CREATING,
  LIBRARY_VIEW_PREVIEW,
  LIBRARY_VIEW_ERROR,
  LIBRARY_TYPE_SHOWIT,
  LIBRARY_TYPE_USER,
  MODAL_DESIGN_IMPORT,
  MAX_DESIGNS_FOR_TRIAL_ERROR_MESSAGE,
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
} = AppConstants;

/**
 * @typedef {import("@/types/services/LibraryServiceTypes.mjs").SiteConfig} SiteConfig
 */

/**
 * Main overview for choosing designs to add to an account.
 * @param {Object} props
 * @param {boolean} props.closeEnabled - Controls if the view can be closed
 * @returns {React.Element}
 */

const LibrarySiteChooserView = ({ closeEnabled = false, folderId }) => {
  const [view, setView] = useState(LIBRARY_VIEW_INITIAL);
  const [libType, setLibType] = useState(LIBRARY_TYPE_SHOWIT);
  const [previewSite, setPreviewSite] = useState(null);
  const [error, setError] = useState(null);
  const [openSubscribe] = useOpenAccount({ path: PATH_SUBSCRIBE });

  const { showitLibDesigns, isFetching: showitLibLoading } = useShowitLibraryDesigns({
    enabled: libType === LIBRARY_TYPE_SHOWIT,
    onError: () => {
      setError(error.message);
    },
  });
  const { userLibDesigns, isFetching: userLibLoading } = useUserLibraryDesigns({
    enabled: libType === LIBRARY_TYPE_USER,
    onError: () => {
      setError(error.message);
    },
  });
  const { sites = [] } = useSites();

  /**
   *
   * @param {SiteConfig} site
   * @param {*} e
   */
  const _createSite = async (site, e) => {
    if (DesignManager.needsToSave()) {
      alert(
        "Current site design is still saving.  Please wait until save is complete before creating another design",
      );
    } else {
      const meta = _.pick(site, ["lib_design_id", "design_id", "pub_id", "export_id", "name"]);
      const creationInfo = prepareCreationData(site, folderId);

      setView(LIBRARY_VIEW_CREATING);

      // Check to make sure the design is the correct version
      try {
        const { sitesBucket } = config;
        const key = site.pub_key !== undefined ? site.pub_key : site.key;
        const sourceJSON = await API.getJSON(`${sitesBucket}/${key}/site.json`);
        const isCorrectDesignVersion = isCorrectVersion(+sourceJSON?.data?.version);
        if (!isCorrectDesignVersion) {
          await migrateDesign({
            key,
            migrationType: MIGRATION_TYPE_SHARED,
          });
        }

        const { site: newSite } = await UserService.createSite(creationInfo);
        UserActions.trackEvent({ event: "site-create", meta });

        queryClient.invalidateQueries({ queryKey: [USER_SITES] });
        SiteActions.loadSite(newSite);
        UIActions.showDesignView();
      } catch (exception) {
        setError(exception);
        setView(LIBRARY_VIEW_ERROR);
      }
    }
  };

  const _preview = (site, e) => {
    setPreviewSite(site);
    setView(LIBRARY_VIEW_PREVIEW);
  };

  const _previewClose = () => {
    setView(LIBRARY_VIEW_INITIAL);
  };

  const _close = () => {
    UIActions.showPreviousView();
  };

  const _showImportDesign = () => {
    UIActions.modalOpen(MODAL_DESIGN_IMPORT);
  };

  const _openStore = () => {
    UserActions.openStore({ location: "", newWindow: true });
  };

  const _getStoreSpace = () => (
    <div className={styles["site-chooser-visit-design-market"]}>
      <div className="design-market-device" />
      <div className={styles["design-market-container"]}>
        <Text variant="title-page" tag="h1">
          Premium Site Designs
        </Text>
        <Text variant="subtitle" tag="h2">
          Now available at the Showit Design Market
        </Text>
        <p style={{ marginBottom: 40 }}>
          <Button size="lg" variant="outline" onClick={_openStore}>
            View Premium Designs
          </Button>
        </p>
      </div>
    </div>
  );

  const _getInitialView = () => {
    let gridView = null;

    if (showitLibLoading || userLibLoading) {
      gridView = (
        <div style={{ textAlign: "center", padding: 20 }}>
          <LoadingSpinner />
        </div>
      );
    } else if (error) {
      gridView = <div style={{ textAlign: "center", padding: 20 }}>{error}</div>;
    } else {
      const siteTiles = [];
      (libType === LIBRARY_TYPE_SHOWIT ? showitLibDesigns : userLibDesigns).forEach((site) => {
        let desktopPreview = null;
        let mobilePreview = `${config.libraryURL}/app/preview-placeholder-mob.jpg`;

        let siteId = null;
        let siteName = null;
        let siteKey = null;

        if (LIBRARY_TYPE_SHOWIT === libType) {
          siteId = `lib${site.lib_design_id}`;
          siteName = site.name;
          siteKey = site.key;
        } else if (LIBRARY_TYPE_USER === libType) {
          siteId = `user${site.design_id}`;
          siteName = site.name;
          siteKey = site.pub_key;
        }

        if (site.has_preview_imgs === 1) {
          desktopPreview = DesignUtils.getPublishedDesignPreviewImageURL(siteKey, LAYOUT_DESKTOP);
          mobilePreview = DesignUtils.getPublishedDesignPreviewImageURL(siteKey, LAYOUT_MOBILE);
        }

        siteTiles.push(
          <LibrarySiteChooserThumbnail
            key={siteId}
            siteId={siteId}
            siteName={siteName}
            desktopPreview={desktopPreview}
            mobilePreview={mobilePreview}
            onClick={() => _preview(site)}
          />,
        );
      }, this);

      gridView = (
        <>
          <div className="site-chooser-grid">{siteTiles}</div>
          <div className="site-chooser-policies">
            By choosing a design you agree to the{" "}
            <a
              href="https://store.showit.co/policies/"
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: "none",
                borderBottom: "none",
                color: "var(--text-base-tertiary)",
              }}
            >
              {" "}
              Design Market Policies
            </a>
            .
          </div>
        </>
      );
    }

    const libBtnActive = libType === LIBRARY_TYPE_SHOWIT ? " active" : "";
    const userBtnActive = libType === LIBRARY_TYPE_USER ? " active" : "";

    const importArea = (
      <div className={styles["add-design-button-container"]}>
        <Button variant="outline" size="lg" onClick={_showImportDesign}>
          <span slot="icon-prefix" className="glyphicon glyphicon-cloud_plus" />
          Add Design To Library
        </Button>
      </div>
    );

    return (
      <div className="site-chooser">
        {sites.length === 0 && (
          <div className={styles["showit-logo"]}>
            <div className="showitNameLogo">
              <ShowitNameLogo marginBottom="0" />
            </div>
          </div>
        )}
        <div className="site-chooser-header" style={{ padding: "50px 0" }}>
          <div
            className="tabset"
            style={{ height: "3.9375rem", justifyContent: "center", padding: "0 50px 0" }}
          >
            <div
              className={`tabset-tab${libBtnActive} ${styles["tabset-tab-designs"]}`}
              onClick={() => setLibType(LIBRARY_TYPE_SHOWIT)}
            >
              Showit Designs
            </div>
            <div
              className={`tabset-tab${userBtnActive} ${styles["tabset-tab-designs"]}`}
              onClick={() => setLibType(LIBRARY_TYPE_USER)}
            >
              Your Designs
            </div>
          </div>
          {closeEnabled ? (
            <span
              className={`glyphicon glyphicon-remove_2 ${styles["close-button"]}`}
              onClick={_close}
            />
          ) : null}
        </div>
        <Text
          data-e2e={uiPrefix("choose-site-header-text")}
          variant="heading-1"
          tag="h1"
          className={styles["choose-site-header-text"]}
        >
          Choose a design to start with
        </Text>
        {importArea}
        {gridView}
        {_getStoreSpace()}
      </div>
    );
  };

  const _getPreviewView = () => {
    let designURL = null;

    if (_.has(previewSite, "design_id")) {
      designURL = DesignUtils.getPreviewURL(previewSite.pub_key);
    } else {
      designURL = DesignUtils.getPreviewURL(previewSite.key);
    }

    return (
      <LibrarySitePreview
        buttonText="Start with this design"
        site={previewSite}
        designURL={designURL}
        onSelect={() => _createSite(previewSite)}
        onClose={_previewClose}
      />
    );
  };

  const _getCreatingSiteView = () => {
    const message = "Your Site Is Being Prepared";
    return <SubviewProcessing message={message} />;
  };

  const _getErrorView = () => {
    const errorView =
      error?.error?.detail === MAX_DESIGNS_FOR_TRIAL_ERROR_MESSAGE ? (
        <InfoMessageModal
          title="Subscription Required"
          message="Only 3 designs allowed when not subscribed."
          closeButtonText="Close"
          closeButtonAction={_close}
          actionButtonText="Subscribe"
          actionButtonAction={openSubscribe}
          showActionButton
          show
          closeButton
          onHide={_close}
        />
      ) : (
        <SubviewError error={error} onClose={_close} />
      );
    return errorView;
  };

  switch (view) {
    case LIBRARY_VIEW_INITIAL:
      return _getInitialView();

    case LIBRARY_VIEW_PREVIEW:
      return _getPreviewView(previewSite);

    case LIBRARY_VIEW_CREATING:
      return _getCreatingSiteView();

    case LIBRARY_VIEW_ERROR:
      return _getErrorView(error);

    default:
      return _getErrorView(error);
  }
};

export default LibrarySiteChooserView;
